<template>
  <div class="encounter-panel stack tight">
    <UiPanel v-if="!embedded" tone="combat">
      <div class="topbar">
        <div>
          <h3 class="title">Encounter</h3>
          <div class="subtitle text-muted stat-mono num">
            Turn {{ formatNumber(encounter.turnNumber ?? 1) }} · {{ encounter.type }} · {{ encounter.areaId }}
          </div>
        </div>
        <div class="topbar-actions">
          <UiChip v-if="encounter.activeTurn" variant="warning">{{ ICONS.states.active }} Active</UiChip>
          <UiButton v-if="canDismiss" size="sm" variant="secondary" @click="emit('dismiss')">
            <template #icon>{{ ICONS.actions.close }}</template>
            Dismiss
          </UiButton>
        </div>
      </div>
      <div class="text-muted stat-mono num">Order: {{ encounter.turnOrder.join(" → ") }}</div>
    </UiPanel>

    <div class="encounter-grid">
      <UiPanel tone="combat" class="col col-left">
        <UiSectionHeader>
          <template #title>Participants</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(encounter.participants.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <div class="participants">
          <div
            v-for="p in encounter.participants"
            :key="p.id"
            class="participant"
            :class="{ 'is-player': p.id === playerId }"
          >
            <div class="participant-head">
              <div class="icon-well" aria-hidden="true">
                {{ p.id === playerId ? ICONS.inspectKinds.player : ICONS.inspectKinds.npc }}
              </div>
              <div class="participant-main">
                <div class="participant-name">{{ p.name }}</div>
                <div class="text-muted stat-mono num">
                  <span>Init {{ formatNumber(p.initiative) }}</span>
                  <template v-if="p.id === playerId">
                    <span class="dot">•</span>
                    <span>You</span>
                  </template>
                </div>
              </div>
              <UiChip variant="muted">{{ p.kind }}</UiChip>
            </div>

            <div class="bars">
              <div class="bar-head">
                <span class="bar-label">{{ ICONS.stats.hp }} HP</span>
                <span class="bar-value stat-mono num">{{ formatParticipantHealth(p) }}</span>
              </div>
              <UiProgressBar :value="getHealthPercent(p)" tone="combat" />

              <div v-if="hasMana(p)" style="margin-top: 0.5rem;">
                <div class="bar-head">
                  <span class="bar-label">{{ ICONS.stats.mana }} Mana</span>
                  <span class="bar-value stat-mono num">{{ formatParticipantMana(p) }}</span>
                </div>
                <UiProgressBar :value="getManaPercent(p)" tone="social" />
              </div>

              <div v-if="p.activeWard && wardTurnsRemaining(p) > 0" class="ward">
                <UiChip variant="muted" class="stat-mono num">
                  {{ ICONS.itemsByType.armor }} {{ wardLabel(p.activeWard.type) }} · {{ formatNumber(wardTurnsRemaining(p)) }}t
                </UiChip>
              </div>
            </div>
          </div>
        </div>
      </UiPanel>

      <div class="col col-center stack tight">
        <UiPanel v-if="styleContext" tone="combat">
          <UiSectionHeader>
            <template #title>Style Matchup</template>
            <template #actions>
              <UiChip :variant="styleContext.matchupVariant">{{ styleContext.matchupText }}</UiChip>
            </template>
          </UiSectionHeader>

          <div class="matchup">
            <div class="matchup-side">
              <UiChip variant="muted">{{ styleContext.player.icon }} {{ styleContext.player.label }}</UiChip>
              <div class="text-muted" style="margin-top: 0.25rem;">You</div>
            </div>
            <div class="matchup-center text-muted">
              <div class="matchup-detail">{{ styleContext.matchupDetail }}</div>
            </div>
            <div class="matchup-side" style="align-items: flex-end;">
              <UiChip variant="muted">{{ styleContext.npc.icon }} {{ styleContext.npc.label }}</UiChip>
              <div class="text-muted" style="margin-top: 0.25rem;">{{ styleContext.npc.name }}</div>
            </div>
          </div>
        </UiPanel>

        <UiPanel v-if="loadout" tone="combat">
          <UiSectionHeader>
            <template #title>Loadout</template>
          </UiSectionHeader>

          <div class="loadout">
            <div class="loadout-row">
              <UiChip variant="muted">Weapon</UiChip>
              <div class="loadout-main">
                <div class="loadout-title">
                  {{ loadout.weaponLabel || "Unarmed" }}
                  <UiChip v-if="loadout.weaponStyle" variant="muted">{{ loadout.weaponStyle }}</UiChip>
                </div>
                <div v-if="loadout.weaponDetail" class="text-muted">{{ loadout.weaponDetail }}</div>
              </div>
            </div>

            <div class="loadout-row">
              <UiChip variant="muted">Quiver</UiChip>
              <div class="loadout-main">
                <div class="loadout-title">
                  {{ loadout.quiverLabel || "No quiver equipped" }}
                  <UiChip v-if="loadout.quiverStatus" :variant="loadout.quiverEmpty ? 'warning' : 'active'">
                    {{ loadout.quiverStatus }}
                  </UiChip>
                </div>
                <div v-if="loadout.quiverDetail" class="text-muted">{{ loadout.quiverDetail }}</div>
                <div v-if="loadout.arrowName" class="text-muted">Arrows: {{ loadout.arrowName }}</div>
                <UiCallout v-if="loadout.quiverWarning" variant="warning" style="margin-top: 0.6rem;">
                  {{ loadout.quiverWarning }}
                </UiCallout>
              </div>
            </div>
          </div>
        </UiPanel>

        <UiPanel tone="combat" class="actions-panel">
          <UiSectionHeader>
            <template #title>{{ isPlayerTurn ? "Your Turn" : "Waiting" }}</template>
            <template #actions>
              <UiChip v-if="isPlayerTurn" :variant="timerChipVariant" class="stat-mono num">
                {{ ICONS.actions.timer }} {{ timerLabel }}
              </UiChip>
              <UiChip v-else variant="muted" class="stat-mono num">—</UiChip>
            </template>
          </UiSectionHeader>

          <UiProgressBar v-if="isPlayerTurn" :value="timerProgressPct" tone="combat" />

          <div v-if="playerParticipant" class="player-vitals">
            <UiChip variant="muted" class="stat-mono num">
              {{ ICONS.stats.hp }} {{ formatParticipantHealth(playerParticipant) }}
            </UiChip>
            <UiChip v-if="hasMana(playerParticipant)" variant="muted" class="stat-mono num">
              {{ ICONS.stats.mana }} {{ formatParticipantMana(playerParticipant) }}
            </UiChip>
          </div>

          <div v-if="recentLog.length" class="recent-log">
            <div class="subheader">Recent</div>
            <div class="recent-log__list">
              <div
                v-for="(line, idx) in recentLog"
                :key="`recent-${idx}`"
                class="log-entry"
                :class="getCombatEntryClass(line)"
              >
                <div class="log-text">{{ line }}</div>
              </div>
            </div>
          </div>

          <div class="stack tight" style="margin-top: 0.85rem;">
            <div class="row wrap" style="justify-content: flex-start;">
              <UiButton tone="combat" size="sm" :disabled="!isPlayerTurn || turnSubmitting" @click="emit('action', 'attack')">
                <template #icon>{{ ICONS.combat.melee }}</template>
                Attack
              </UiButton>
              <UiButton tone="combat" size="sm" variant="secondary" :disabled="!isPlayerTurn || turnSubmitting" @click="emit('action', 'defend')">
                Defend
              </UiButton>
              <UiButton tone="combat" size="sm" variant="secondary" :disabled="!isPlayerTurn || turnSubmitting" @click="emit('action', 'flee')">
                Flee
              </UiButton>
            </div>

            <template v-if="isPlayerTurn && readiedSpells.length">
              <div class="subheader">Cast Spell</div>
              <div class="row wrap">
                <UiButton
                  v-for="spell in readiedSpells"
                  :key="spell.id"
                  size="sm"
                  variant="ghost"
                  tone="combat"
                  :disabled="turnSubmitting || !canCast(spell)"
                  :title="spell.manaCost ? `Costs ${formatNumber(spell.manaCost)} mana` : 'Cast'"
                  @click="emitCast(spell.id)"
                >
                  <template #icon>{{ ICONS.skills.magic }}</template>
                  {{ spell.name }}
                  <span v-if="spell.manaCost" class="stat-mono num">({{ formatNumber(spell.manaCost) }})</span>
                </UiButton>
              </div>
            </template>

            <UiPanel tone="combat" etched>
              <UiSectionHeader>
                <template #title>Ammo</template>
              </UiSectionHeader>

              <div class="ammo">
                <div class="ammo-block">
                  <div class="subheader">Reload quiver</div>
                  <div class="row wrap" style="justify-content: space-between;">
                    <select
                      v-model="selectedReloadItemId"
                      class="ui-input stat-mono"
                      :disabled="!isPlayerTurn || turnSubmitting || !reloadOptions.length || canReload === false"
                    >
                      <option v-for="opt in reloadOptions" :key="opt.itemId" :value="opt.itemId">
                        {{ opt.label }}
                      </option>
                    </select>
                    <UiButton
                      tone="combat"
                      size="sm"
                      :disabled="!isPlayerTurn || turnSubmitting || !selectedReloadItemId || canReload === false"
                      @click="emitReload"
                    >
                      Reload
                    </UiButton>
                  </div>
                  <div v-if="reloadHint" class="text-muted" style="margin-top: 0.35rem;">{{ reloadHint }}</div>
                </div>

                <div class="ammo-block">
                  <div class="subheader">Hot-swap quiver</div>
                  <div v-if="hotSwapOptions.length" class="swap-grid">
                    <UiButton
                      v-for="swap in hotSwapOptions"
                      :key="swap.instanceId"
                      size="sm"
                      variant="ghost"
                      tone="combat"
                      :disabled="!isPlayerTurn || turnSubmitting || canHotSwap === false"
                      :title="swap.status"
                      @click="emit('hot-swap', swap.instanceId)"
                    >
                      {{ swap.label }}
                      <span class="text-muted" style="margin-left: 0.4rem;">{{ swap.status }}</span>
                    </UiButton>
                  </div>
                  <div v-else class="text-muted">No loaded spare quivers detected.</div>
                  <UiCallout v-if="hotSwapDisabledReason" variant="warning" style="margin-top: 0.6rem;">
                    {{ hotSwapDisabledReason }}
                  </UiCallout>
                </div>
              </div>
            </UiPanel>

            <UiPanel v-if="isPlayerTurn" tone="combat" etched>
              <UiSectionHeader>
                <template #title>Bonus Action</template>
                <template #actions>
                  <UiChip :variant="bonusActionUsed ? 'blocked' : 'active'">{{ bonusActionUsed ? "Used" : "Ready" }}</UiChip>
                </template>
              </UiSectionHeader>

              <div v-if="consumables.length" class="row wrap" style="margin-top: 0.6rem;">
                <UiButton
                  v-for="item in consumables"
                  :key="item.itemId"
                  size="sm"
                  variant="secondary"
                  :disabled="usingItemId === item.itemId || canUseConsumable === false"
                  :title="canUseConsumable === false && useDisabledReason ? useDisabledReason : 'Use consumable'"
                  @click="emit('use-consumable', item.itemId)"
                >
                  <template #icon>{{ ICONS.itemsByType.consumable }}</template>
                  {{ item.item?.name || item.itemId }}
                  <span class="stat-mono num">(×{{ formatNumber(item.quantity) }})</span>
                </UiButton>
              </div>
              <div v-else class="text-muted" style="margin-top: 0.6rem;">No consumables available.</div>
            </UiPanel>
          </div>
        </UiPanel>
      </div>

      <UiPanel tone="combat" class="col col-right">
        <UiSectionHeader>
          <template #title>Combat Log</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(reversedLog.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <div ref="combatLogRef" class="combat-log scroll">
          <div v-if="reversedLog.length === 0" class="text-muted">No log entries yet.</div>
          <div v-else class="log-list">
            <div
              v-for="(line, idx) in reversedLog"
              :key="idx"
              class="log-entry"
              :class="getCombatEntryClass(line)"
            >
              <div class="log-text">{{ line }}</div>
            </div>
          </div>
        </div>
      </UiPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import type { Encounter, InventoryEntry, Spell, TurnAction } from "@idle-rpg/shared";
import { ICONS } from "../../lib/icons";
import { formatNumber } from "../../lib/format";
import UiButton from "../ui/UiButton.vue";
import UiCallout from "../ui/UiCallout.vue";
import UiChip from "../ui/UiChip.vue";
import UiPanel from "../ui/UiPanel.vue";
import UiProgressBar from "../ui/UiProgressBar.vue";
import UiSectionHeader from "../ui/UiSectionHeader.vue";

type CombatStyle = Encounter["participants"][number]["combatStyle"];

const props = withDefaults(
  defineProps<{
    encounter: Encounter;
    playerId: string;
    embedded?: boolean;
    readiedSpells?: Spell[];
    turnTimeRemaining?: number | null;
    localLogLines?: string[];
    consumables?: InventoryEntry[];
    canUseConsumable?: boolean;
    useDisabledReason?: string | null;
    usingItemId?: string | null;
    bonusActionUsed?: boolean;
    loadout?: {
      weaponLabel?: string | null;
      weaponStyle?: string | null;
      weaponDetail?: string | null;
      quiverLabel?: string | null;
      quiverStatus?: string | null;
      quiverDetail?: string | null;
      quiverWarning?: string | null;
      quiverEmpty?: boolean;
      quiverCapacity?: number | null;
      quiverFillPercent?: number;
      arrowName?: string | null;
    } | null;
    reloadOptions?: Array<{ itemId: string; label: string; quantity: number }>;
    canReload?: boolean;
    reloadDisabledReason?: string | null;
    hotSwapOptions?: Array<{ instanceId: string; label: string; status: string }>;
    canHotSwap?: boolean;
    hotSwapDisabledReason?: string | null;
    turnSubmitting?: boolean;
  }>(),
  {
    embedded: false,
    readiedSpells: () => [],
    turnTimeRemaining: null,
    localLogLines: () => [],
    consumables: () => [],
    canUseConsumable: true,
    useDisabledReason: null,
    usingItemId: null,
    bonusActionUsed: false,
    loadout: null,
    reloadOptions: () => [],
    canReload: true,
    reloadDisabledReason: null,
    hotSwapOptions: () => [],
    canHotSwap: true,
    hotSwapDisabledReason: null,
    turnSubmitting: false
  }
);

const emit = defineEmits<{
  dismiss: [];
  action: [action: TurnAction];
  "cast-spell": [spellId: string];
  "use-consumable": [itemId: string];
  reload: [itemId: string | null];
  "hot-swap": [instanceId: string];
}>();

const encounter = computed(() => props.encounter);
const playerId = computed(() => props.playerId);
const embedded = computed(() => props.embedded);
const readiedSpells = computed(() => props.readiedSpells ?? []);
const consumables = computed(() => props.consumables ?? []);
const canUseConsumable = computed(() => props.canUseConsumable !== false);
const useDisabledReason = computed(() => props.useDisabledReason ?? null);
const usingItemId = computed(() => props.usingItemId ?? null);
const bonusActionUsed = computed(() => props.bonusActionUsed === true);
const loadout = computed(() => props.loadout ?? null);
const reloadOptions = computed(() => props.reloadOptions ?? []);
const canReload = computed(() => props.canReload !== false);
const hotSwapOptions = computed(() => props.hotSwapOptions ?? []);
const canHotSwap = computed(() => props.canHotSwap !== false);
const hotSwapDisabledReason = computed(() => props.hotSwapDisabledReason ?? null);
const turnSubmitting = computed(() => props.turnSubmitting === true);

const isPlayerTurn = computed(() => encounter.value.activeTurn === playerId.value);
const canDismiss = computed(() => !encounter.value.activeTurn);

const combatLogRef = ref<HTMLElement | null>(null);
const selectedReloadItemId = ref<string | null>(null);

const playerParticipant = computed(() => encounter.value.participants.find((p) => p.id === playerId.value) || null);
const npcParticipant = computed(() => encounter.value.participants.find((p) => p.kind === "npc") || null);

function hasMana(participant: Encounter["participants"][number]): boolean {
  const max = Math.max(0, Math.floor(Number((participant as any).maxMana ?? 0)));
  const mana = Math.max(0, Math.floor(Number((participant as any).mana ?? 0)));
  return max > 0 || mana > 0;
}

function getManaPercent(participant: Encounter["participants"][number]): number {
  const max = Math.max(1, Math.floor(Number((participant as any).maxMana ?? 1)));
  const mana = Math.max(0, Math.floor(Number((participant as any).mana ?? 0)));
  return Math.max(0, Math.min(100, (mana / max) * 100));
}

function formatParticipantMana(participant: Encounter["participants"][number]): string {
  const max = Math.max(0, Math.floor(Number((participant as any).maxMana ?? 0)));
  const mana = Math.max(0, Math.floor(Number((participant as any).mana ?? 0)));
  return max > 0
    ? `${formatNumber(mana)} / ${formatNumber(max)} mana`
    : `${formatNumber(mana)} mana`;
}

function getHealthPercent(participant: Encounter["participants"][number]): number {
  const debuff = Math.max(0, participant.maxHpDebuff || 0);
  const maxHp = Math.max(1, (participant.maxHp || participant.hp) - debuff);
  if (maxHp === 0) return 0;
  return Math.max(0, Math.min(100, (participant.hp / maxHp) * 100));
}

function formatParticipantHealth(participant: Encounter["participants"][number]): string {
  const debuff = Math.max(0, participant.maxHpDebuff || 0);
  const effectiveMax = Math.max(1, (participant.maxHp || participant.hp) - debuff);
  const temp = Math.max(0, participant.tempHp || 0);
  const parts = [`${formatNumber(participant.hp)} / ${formatNumber(effectiveMax)} HP`];
  if (debuff) parts.push(`(-${formatNumber(debuff)})`);
  if (temp) parts.push(`(+${formatNumber(temp)})`);
  return parts.join(" ");
}

function wardTurnsRemaining(participant: Encounter["participants"][number]): number {
  const ward = (participant as any).activeWard as { type: string; expiresAtTurn: number } | null | undefined;
  if (!ward) return 0;
  const nowTurn = Math.max(1, Math.floor(Number((encounter.value as any).turnNumber ?? 1)));
  return Math.max(0, Math.floor(Number(ward.expiresAtTurn || 0)) - nowTurn);
}

function wardLabel(type: string): string {
  switch (type) {
    case "fire":
      return "Fire";
    case "frost":
      return "Frost";
    case "arcane":
      return "Arcane";
    case "piercing":
      return "Piercing";
    default:
      return type;
  }
}

function canCast(spell: Spell): boolean {
  const p = playerParticipant.value;
  if (!p) return false;
  const mana = Math.max(0, Math.floor(Number((p as any).mana ?? 0)));
  return mana >= Math.max(0, Math.floor(Number(spell.manaCost || 0)));
}

function emitCast(spellId: string) {
  if (!isPlayerTurn.value) return;
  if (turnSubmitting.value) return;
  emit("cast-spell", spellId);
}

function styleMeta(style: CombatStyle | undefined | null) {
  switch (style) {
    case "melee":
      return { style: "melee" as CombatStyle, label: "Melee", icon: ICONS.combat.melee };
    case "ranged":
      return { style: "ranged" as CombatStyle, label: "Ranged", icon: ICONS.combat.ranged };
    case "magic":
      return { style: "magic" as CombatStyle, label: "Magic", icon: ICONS.combat.magic };
    case "unarmed":
    default:
      return { style: "unarmed" as CombatStyle, label: "Unarmed", icon: ICONS.combat.unarmed };
  }
}

function resolveStyleMatchup(
  attacker: CombatStyle | undefined | null,
  defender: CombatStyle | undefined | null
): "advantage" | "disadvantage" | "none" {
  if (!attacker || !defender || attacker === "unarmed" || defender === "unarmed") return "none";
  if (attacker === "melee" && defender === "ranged") return "advantage";
  if (attacker === "melee" && defender === "magic") return "disadvantage";
  if (attacker === "ranged" && defender === "magic") return "advantage";
  if (attacker === "ranged" && defender === "melee") return "disadvantage";
  if (attacker === "magic" && defender === "melee") return "advantage";
  if (attacker === "magic" && defender === "ranged") return "disadvantage";
  return "none";
}

const styleContext = computed(() => {
  if (!playerParticipant.value || !npcParticipant.value) return null;
  const player = styleMeta(playerParticipant.value.combatStyle);
  const npc = styleMeta(npcParticipant.value.combatStyle);
  const outcome = resolveStyleMatchup(player.style, npc.style);
  if (outcome === "advantage") {
    return {
      player: { ...player, name: playerParticipant.value.name },
      npc: { ...npc, name: npcParticipant.value.name },
      matchupText: "Advantage",
      matchupDetail: `${player.label} pressures ${npc.label.toLowerCase()}.`,
      matchupVariant: "active" as const
    };
  }
  if (outcome === "disadvantage") {
    return {
      player: { ...player, name: playerParticipant.value.name },
      npc: { ...npc, name: npcParticipant.value.name },
      matchupText: "Disadvantage",
      matchupDetail: `${npc.label} counters ${player.label.toLowerCase()}.`,
      matchupVariant: "danger" as const
    };
  }
  return {
    player: { ...player, name: playerParticipant.value.name },
    npc: { ...npc, name: npcParticipant.value.name },
    matchupText: "Neutral",
    matchupDetail: "No style advantage active.",
    matchupVariant: "muted" as const
  };
});

const reloadHint = computed(() => {
  if (props.reloadDisabledReason) return props.reloadDisabledReason;
  if (!reloadOptions.value.length) return "No loose arrows in your inventory.";
  return "Load arrows from your inventory into the equipped quiver.";
});

watch(
  () => reloadOptions.value,
  (opts) => {
    if (opts.length > 0) {
      const current = opts.find((o) => o.itemId === selectedReloadItemId.value);
      selectedReloadItemId.value = current ? current.itemId : opts[0].itemId;
    } else {
      selectedReloadItemId.value = null;
    }
  },
  { immediate: true }
);

function emitReload() {
  if (!selectedReloadItemId.value) return;
  if (!canReload.value) return;
  if (!isPlayerTurn.value) return;
  emit("reload", selectedReloadItemId.value);
}

const timerLabel = computed(() => {
  if (!isPlayerTurn.value) return "—";
  const t = props.turnTimeRemaining ?? null;
  if (t === null) return "—";
  return `${Math.max(0, Math.floor(t))}s`;
});

const timerChipVariant = computed(() => {
  const t = props.turnTimeRemaining ?? 999;
  if (t <= 5) return "danger";
  if (t <= 12) return "warning";
  return "muted";
});

const timerProgressPct = computed(() => {
  const t = props.turnTimeRemaining ?? null;
  if (t === null) return 0;
  const progress = Math.max(0, Math.min(30, t)) / 30;
  return Math.max(0, Math.min(100, Math.round(progress * 100)));
});

const reversedLog = computed(() => {
  const log = [...(encounter.value.log || []), ...((props.localLogLines || []).filter(Boolean))];
  return [...log].reverse();
});
const recentLog = computed(() => reversedLog.value.slice(0, 3));

function stripLeadingPictographs(line: string): string {
  try {
    return line.replace(/^[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D]+\s*/u, "");
  } catch {
    return line.replace(/^[^A-Za-z0-9]+\s*/, "");
  }
}

function getCombatEntryClass(logLine: string): string {
  const participant = encounter.value.participants.find((p) => p.id === playerId.value);
  if (!participant) return "";

  const playerName = participant.name;
  const lines = logLine.split("\n").map((l) => l.trim());
  const normalized = lines.map(stripLeadingPictographs);
  const firstLine = normalized[0] || "";

  if (firstLine.includes("regenerated")) {
    return firstLine.includes(playerName) ? "is-good" : "";
  }

  if (firstLine.includes("is victorious") && firstLine.includes(playerName)) return "is-good";
  if (firstLine.includes("is defeated") && firstLine.includes(playerName)) return "is-bad";

  if (firstLine.includes("flees from battle")) {
    return firstLine.includes(playerName) ? "is-bad" : "is-good";
  }

  const attackMatch = firstLine.match(/^(.+?)\s+attacks\s+(.+)/);
  if (attackMatch) {
    const attacker = attackMatch[1];
    const playerIsAttacker = attacker === playerName;
    const isHit = normalized.some((l) => l.includes("HIT") || l.includes("CRITICAL HIT"));
    const isMiss = normalized.some((l) => l.includes("MISS") || l.includes("CRITICAL MISS"));

    if (isHit) return playerIsAttacker ? "is-good" : "is-bad";
    if (isMiss) return playerIsAttacker ? "is-bad" : "is-good";
  }

  return "";
}

watch(
  () => encounter.value.log,
  async () => {
    await nextTick();
    if (combatLogRef.value) combatLogRef.value.scrollTop = 0;
  },
  { deep: true }
);
</script>

<style scoped>
.encounter-panel {
  min-width: 0;
}

.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  justify-content: flex-end;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  margin-top: 0.15rem;
  font-size: 0.9rem;
}

.encounter-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr) minmax(0, 1fr);
  gap: var(--space-3);
  align-items: start;
}

.participants {
  margin-top: var(--space-sm);
  display: grid;
  gap: 0.65rem;
}

.participant {
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  padding: 0.75rem 0.8rem;
}

.participant.is-player {
  border-color: color-mix(in srgb, var(--accent) 38%, var(--glass-border-0));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent) 16%, transparent);
}

.participant-head {
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  justify-content: space-between;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.participant-main {
  min-width: 0;
  flex: 1 1 auto;
  display: grid;
  gap: 0.15rem;
}

.participant-name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dot {
  opacity: 0.7;
  padding: 0 0.35rem;
}

.bars {
  margin-top: 0.55rem;
}

.bar-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.bar-label {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.ward {
  margin-top: 0.55rem;
}

.matchup {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr);
  gap: var(--space-md);
  align-items: center;
}

.matchup-side {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
}

.matchup-detail {
  font-family: var(--font-mono);
  font-size: 0.9rem;
}

.subheader {
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.player-vitals {
  margin-top: 0.65rem;
  display: none;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.recent-log {
  margin-top: 0.75rem;
  display: none;
  gap: 0.5rem;
}

.recent-log__list {
  display: grid;
  gap: 0.45rem;
}

.loadout {
  margin-top: var(--space-sm);
  display: grid;
  gap: 0.75rem;
}

.loadout-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.65rem;
  align-items: start;
}

.loadout-main {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.loadout-title {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  font-weight: 750;
}

.ammo {
  margin-top: 0.75rem;
  display: grid;
  gap: 1rem;
}

.swap-grid {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.combat-log {
  margin-top: var(--space-sm);
  max-height: 560px;
}

.log-list {
  display: grid;
  gap: 0.65rem;
}

.log-entry {
  border: 1px solid var(--glass-border-0);
  border-radius: var(--radius-2);
  padding: 0.65rem 0.75rem;
  background: var(--glass-0);
}

.log-entry.is-good {
  border-left: 3px solid var(--state-active);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-active) 12%, transparent), var(--glass-0) 42%);
}

.log-entry.is-bad {
  border-left: 3px solid var(--state-danger);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-danger) 12%, transparent), var(--glass-0) 42%);
}

.log-text {
  white-space: pre-line;
  line-height: 1.35;
}

.ui-input {
  width: min(360px, 100%);
}

@media (max-width: 980px) {
  .encounter-grid {
    grid-template-columns: 1fr;
  }

  .combat-log {
    max-height: 420px;
  }
}

@media (max-width: 900px) {
  .player-vitals {
    display: flex;
  }

  .recent-log {
    display: grid;
  }

  .recent-log .log-entry {
    padding: 0.5rem 0.6rem;
  }

  .recent-log .log-text {
    font-size: 0.9rem;
  }
}
</style>
